import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";

import { enrollInCourse } from "../api/courseApi";
import { COURSE_MESSAGES } from "../messages";

// Query param that tells the course page to enroll right after login
const AUTO_ENROLL_PARAM = "enroll";

export const useEnrollment = (courseId?: string) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { lang } = useLanguage();
    const { isAuthenticated, loading: authLoading } = useAuth();

    const [enrolling, setEnrolling] = useState(false);
    const [enrollError, setEnrollError] = useState("");
    const autoEnrollHandled = useRef(false);

    const enroll = useCallback(async () => {
        // Wait until we know whether the user is logged in
        if (!courseId || authLoading || enrolling) return;

        if (!isAuthenticated) {
            // After login, return to this course with ?enroll=1 to finish automatically.
            // The whole return URL is encoded so "?enroll=1" stays inside the redirect value.
            const returnTo = `${ROUTES.course(lang, courseId)}?${AUTO_ENROLL_PARAM}=1`;
            navigate(`${ROUTES.auth(lang)}?redirect=${encodeURIComponent(returnTo)}`);
            return;
        }

        setEnrolling(true);
        setEnrollError("");

        try {
            await enrollInCourse(courseId);
            navigate(ROUTES.myCourseContent(lang, courseId));
        } catch (error) {
            console.error("Enrollment failed:", error);
            const serverMessage = axios.isAxiosError<{ message?: string }>(error)
                ? error.response?.data?.message
                : undefined;
            setEnrollError(serverMessage || COURSE_MESSAGES.enrollFailed);
        } finally {
            setEnrolling(false);
        }
    }, [courseId, authLoading, enrolling, isAuthenticated, lang, navigate]);

    // Back from the login page with ?enroll=1: enroll once, automatically.
    // The ref prevents a second request (React StrictMode runs effects twice in development).
    useEffect(() => {
        if (authLoading || !isAuthenticated || autoEnrollHandled.current) return;
        if (searchParams.get(AUTO_ENROLL_PARAM) !== "1") return;

        autoEnrollHandled.current = true;
        void enroll();
    }, [authLoading, isAuthenticated, searchParams, enroll]);

    return { enroll, enrolling, enrollError };
};